import { useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import useDebounce from "../../../../hooks/useDebounce";
import useHomeFlow from "../../../../hooks/useHomeFlowSimple";
import useHomeFormData from "../../../../hooks/useHomeFormData";
import useLanguageTranslate from "../../../../hooks/useLanguageTranslate";
import BottomStickyBtnLayout from "../../../../pages/motorInsurancePages/newMotorPolicy/Layouts/BottomStickyBtnLayout";
import SignInModal from "../../../../pages/motorInsurancePages/newMotorPolicy/planPages/signInModal";
import {
  fetchBlocksAndAreasList,
  selectBlocksAndAreas,
  selectBlocksAndAreasLoader,
} from "../../../../redux/features/constants/blocksAndAreasListSlice";
import { selectHomeFormData } from "../../../../redux/features/homeFormSlice";
import { selectLoginTokenInfo } from "../../../../redux/features/loginRegisterSlice";
import {
  clearHomeQuote,
  fetchHomeQuote,
  fetchPartyCode,
  selectHomeQuote,
  selectHomeQuoteError,
  selectHomeQuoteLoading,
  selectPartyCode,
} from "../../../../redux/features/quoteSlice";
import Input from "../../../common/Input/Input";
import SelectInput from "../../../common/selectInput/selectInput";
import HomeSummaryModal from "../../../newHomePolicy/modals/homeSummaryModal";
import TitleDescription from "../../../newMotorPolicy/titleDescription/titleDescription";
import NewHomeJourneyLayout from "../../layouts/NewHomeJourneyLayout";

const AddressDetails = () => {
  const translate = useLanguageTranslate();
  const dispatch = useDispatch();
  const { i18n } = useTranslation();
  const isRTL = i18n.resolvedLanguage === "ar";
  const { handleNextSubstep, handlePrevSubstep } = useHomeFlow();
  const { formData: savedFormData, saveHomeDataInRedux } =
    useHomeFormData("addressDetails");

  // Check login status
  const loginTokenInfo = useSelector(selectLoginTokenInfo);
  const isLoggedIn = !!loginTokenInfo;

  // Get party code from Redux
  const partyCode = useSelector(selectPartyCode);

  // Get all home form data for premium calculation
  const allHomeFormData = useSelector(selectHomeFormData);

  // Get blocks and areas data from Redux store
  const blocksAndAreasData = useSelector(selectBlocksAndAreas);
  const blocksAndAreasLoading = useSelector(selectBlocksAndAreasLoader);

  // Get home quote data from Redux store
  const reduxQuoteData = useSelector(selectHomeQuote);
  const reduxQuoteLoading = useSelector(selectHomeQuoteLoading);
  const reduxQuoteError = useSelector(selectHomeQuoteError);

  // Form state
  const [formData, setFormData] = useState({
    buildingNumber: "",
    roadNumber: "",
    blockNumber: "", // User-entered block number
    areaCode: "", // Auto-populated area code based on block
    areaName: "", // Auto-populated area name for display
    buildingAge: "",
  });

  // Debounce all input fields to prevent API calls on every keystroke
  const debouncedBuildingNumber = useDebounce(formData.buildingNumber, 3000);
  const debouncedRoadNumber = useDebounce(formData.roadNumber, 3000);
  const debouncedBlockNumber = useDebounce(formData.blockNumber, 500); // Shorter debounce for block lookup
  const debouncedBuildingAge = useDebounce(formData.buildingAge, 3000);

  // Error state
  const [errors, setErrors] = useState({});

  // Modal state
  const [showSignInModal, setShowSignInModal] = useState(false);
  const [showHomeSummaryModal, setShowHomeSummaryModal] = useState(false);

  // Use Redux quote data (keep local variables for backward compatibility)
  const isLoadingQuote = reduxQuoteLoading;
  const quoteData = reduxQuoteData;
  const quoteError = reduxQuoteError;

  useEffect(() => {
    if (quoteError) {
      toast.error(
        translate(
          "Failed to get a quote. Please try again.",
          "فشل في الحصول على عرض السعر. يرجى المحاولة مرة أخرى."
        )
      );
    }
  }, [quoteError]);

  // True as soon as the user edits any field; reset when a fresh quote arrives
  const [fieldEdited, setFieldEdited] = useState(false);

  // Track last fetched values to prevent infinite loops
  const lastFetchedValues = useRef(null);

  // Skip the homeDetails-change effect on initial mount
  const homeDetailsInitialized = useRef(false);

  // Initialize form with saved data - run only once on mount
  useEffect(() => {
    if (savedFormData && Object.keys(savedFormData).length > 0) {
      setFormData({
        buildingNumber: savedFormData.buildingNumber || "",
        roadNumber: savedFormData.roadNumber || "",
        blockNumber: savedFormData.blockNumber || "",
        areaCode: savedFormData.areaCode || "",
        areaName: savedFormData.areaName || "",
        buildingAge: savedFormData.buildingAge || "",
      });

      // If a valid quote already exists (user navigated back from next screen),
      // pre-initialize the ref with saved values so we don't re-fetch unnecessarily
      if (reduxQuoteData && !reduxQuoteError) {
        lastFetchedValues.current = {
          buildingNumber: savedFormData.buildingNumber || "",
          roadNumber: savedFormData.roadNumber || "",
          blockNumber: savedFormData.blockNumber || "",
          buildingAge: savedFormData.buildingAge || "",
          areaCode: savedFormData.areaCode || "",
          buildingValue: allHomeFormData.homeDetails?.buildingValue,
        };
      }
    }
  }, []);

  // Fetch blocks and areas data on component mount
  useEffect(() => {
    if (!blocksAndAreasData) {
      dispatch(fetchBlocksAndAreasList());
    }
  }, [dispatch, blocksAndAreasData]);

  // Fetch party code for logged-in users
  useEffect(() => {
    if (isLoggedIn && !partyCode && loginTokenInfo?.cpr) {
      console.log(
        "Home Policy - Fetching party code for logged-in user:",
        loginTokenInfo.cpr
      );
      dispatch(fetchPartyCode(loginTokenInfo.cpr));
    }
  }, [isLoggedIn, partyCode, loginTokenInfo?.cpr, dispatch]);

  // Get available blocks from API response
  const availableBlocks = useMemo(() => {
    if (
      !blocksAndAreasData?.blocks ||
      !Array.isArray(blocksAndAreasData.blocks)
    )
      return [];
    return blocksAndAreasData.blocks;
  }, [blocksAndAreasData]);

  // Transform blocks data for SelectInput component
  const blockOptions = useMemo(() => {
    if (!availableBlocks || !Array.isArray(availableBlocks)) return [];

    return availableBlocks.map((block) => ({
      id: block,
      value: block.toString(),
      label: block.toString(),
    }));
  }, [availableBlocks]);

  // Find area for entered block number
  const findAreaByBlock = useMemo(() => {
    return (blockNumber) => {
      if (
        !blocksAndAreasData?.areaBlocks ||
        !Array.isArray(blocksAndAreasData.areaBlocks) ||
        !blockNumber
      ) {
        return null;
      }

      const result = blocksAndAreasData.areaBlocks.find((item) => {
        return item.block === blockNumber.toString();
      });

      return result;
    };
  }, [blocksAndAreasData]);

  // Auto-populate area when debounced block number changes
  useEffect(() => {
    if (debouncedBlockNumber) {
      const areaData = findAreaByBlock(debouncedBlockNumber);

      if (areaData) {
        setFormData((prev) => {
          // Only update if the area has actually changed
          if (prev.areaCode === areaData.code) {
            return prev;
          }
          return {
            ...prev,
            areaCode: areaData.code,
            areaName: translate(areaData.AreaEn, areaData.AreaAr),
          };
        });
      } else {
        // Clear area if block not found
        setFormData((prev) => {
          if (prev.areaCode === "" && prev.areaName === "") {
            return prev;
          }
          return {
            ...prev,
            areaCode: "",
            areaName: "",
          };
        });
      }
    } else {
      // Clear area if no block entered
      setFormData((prev) => {
        if (prev.areaCode === "" && prev.areaName === "") {
          return prev;
        }
        return {
          ...prev,
          areaCode: "",
          areaName: "",
        };
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedBlockNumber, findAreaByBlock]);

  // Check if form is complete and valid, then automatically call API - WITH DEBOUNCE
  useEffect(() => {
    const isFormComplete =
      debouncedBuildingNumber.trim() && // Use debounced value
      debouncedRoadNumber.trim() && // Use debounced value
      debouncedBlockNumber.trim() && // Use debounced value
      debouncedBuildingAge.trim() && // Use debounced value
      formData.areaCode && // Area must be auto-populated
      allHomeFormData.homeDetails?.buildingValue; // Must have home details

    const isFormValid = () => {
      // Quick validation check without setting errors
      if (
        !debouncedBuildingNumber.trim() || // Use debounced value
        !debouncedRoadNumber.trim() || // Use debounced value
        !debouncedBlockNumber.trim() || // Use debounced value
        !debouncedBuildingAge.trim() // Use debounced value
      ) {
        return false;
      }

      if (
        parseInt(debouncedBuildingAge) <= 0 || // Use debounced value
        parseInt(debouncedBuildingAge) > 15 // Use debounced value
      ) {
        return false;
      }

      // Check if block is valid (only if we have blocks data loaded)
      if (
        availableBlocks.length > 0 &&
        !availableBlocks.includes(debouncedBlockNumber) // Use debounced value
      ) {
        return false;
      }

      // Check if area code is populated for the block
      if (!formData.areaCode) {
        return false;
      }

      // CRITICAL: Check if there are any validation errors
      if (
        errors.buildingNumber ||
        errors.roadNumber ||
        errors.blockNumber ||
        errors.buildingAge
      ) {
        console.log(
          "Home Policy - Validation errors present, skipping quote fetch:",
          errors
        );
        return false;
      }

      return true;
    };

    // Create current values snapshot for comparison
    const currentValues = {
      buildingNumber: debouncedBuildingNumber,
      roadNumber: debouncedRoadNumber,
      blockNumber: debouncedBlockNumber,
      buildingAge: debouncedBuildingAge,
      areaCode: formData.areaCode,
      buildingValue: allHomeFormData.homeDetails?.buildingValue,
    };

    // Check if values have changed since last fetch
    const valuesChanged =
      !lastFetchedValues.current ||
      JSON.stringify(lastFetchedValues.current) !==
        JSON.stringify(currentValues);

    // Only call API if form is complete, valid, not already loading, and values have changed
    // For logged-in users, wait for party code to be fetched first
    const shouldFetchQuote =
      isFormComplete && isFormValid() && !isLoadingQuote && valuesChanged;
    const partyCodeReady = !isLoggedIn || partyCode; // For guests, always ready. For logged-in users, wait for party code

    if (shouldFetchQuote && partyCodeReady) {
      console.log(
        "Home Policy - Conditions met for quote fetch. Values changed:",
        valuesChanged,
        "Party code ready:",
        partyCodeReady
      );
      // Clear existing quote data before fetching new one
      if (quoteData) {
        dispatch(clearHomeQuote());
      }
      // Store current values as last fetched
      lastFetchedValues.current = currentValues;
      getQuote();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    debouncedBuildingNumber, // Use debounced value
    debouncedRoadNumber, // Use debounced value
    debouncedBlockNumber, // Use debounced value
    debouncedBuildingAge, // Use debounced value
    formData.areaCode,
    allHomeFormData.homeDetails?.buildingValue,
    isLoadingQuote,
    partyCode,
    isLoggedIn,
    errors.buildingNumber,
    errors.roadNumber,
    errors.blockNumber,
    errors.buildingAge,
  ]);

  // Reset quote data when home details change (from previous step)
  // Skip on initial mount — only clear if homeDetails actually change after mount
  useEffect(() => {
    if (!homeDetailsInitialized.current) {
      homeDetailsInitialized.current = true;
      return;
    }
    if (quoteData) {
      dispatch(clearHomeQuote());
    }
  }, [
    allHomeFormData.homeDetails?.buildingValue,
    allHomeFormData.homeDetails?.accidentalDamage,
    allHomeFormData.homeDetails?.landslideSubsidence,
    allHomeFormData.homeDetails?.contentsDetails,
    allHomeFormData.homeDetails?.contentsValue,
    allHomeFormData.homeDetails?.personalBelongingsValue,
    allHomeFormData.homeDetails?.contentsAccidentalDamage,
    allHomeFormData.homeDetails?.contentsLandslideSubsidence,
    dispatch,
  ]);

  // When a fresh quote arrives, clear the edited flag so the button re-enables
  useEffect(() => {
    if (quoteData) {
      setFieldEdited(false);
    }
  }, [quoteData]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;

    // Immediately disable Continue until the new quote is fetched
    setFieldEdited(true);

    // Clear error for this field when user starts typing
    setErrors((prevErrors) => ({
      ...prevErrors,
      [name]: "",
    }));

    // For building number - allow both letters and numbers
    if (name === "buildingNumber") {
      // Allow alphanumeric characters only
      const cleanValue = value.replace(/[^a-zA-Z0-9]/g, "");
      setFormData((prev) => ({
        ...prev,
        [name]: cleanValue,
      }));
    } else if (name === "roadNumber") {
      // Allow numbers only for road number
      const cleanValue = value.replace(/[^0-9]/g, "");
      setFormData((prev) => ({
        ...prev,
        [name]: cleanValue,
      }));
    } else if (name === "buildingAge") {
      // Real-time validation for Building Age
      setFormData((prev) => ({
        ...prev,
        [name]: value,
      }));

      // Real-time validation - show error immediately if value > 15
      if (value.trim() !== "") {
        const ageValue = parseInt(value);
        if (!isNaN(ageValue)) {
          if (ageValue <= 0) {
            setErrors((prevErrors) => ({
              ...prevErrors,
              buildingAge: translate(
                "This field must be greater than 0",
                "يجب أن يكون هذا الحقل أكبر من 0"
              ),
            }));
          } else if (ageValue > 15) {
            setErrors((prevErrors) => ({
              ...prevErrors,
              buildingAge: translate(
                "The value must not exceed 15.",
                "يجب ألا تتجاوز القيمة 15."
              ),
            }));
          }
        }
      }
    } else {
      setFormData((prev) => ({
        ...prev,
        [name]: value,
      }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.buildingNumber.trim()) {
      newErrors.buildingNumber = translate(
        "This field is required.",
        "هذا الحقل مطلوب"
      );
    }

    if (!formData.roadNumber.trim()) {
      newErrors.roadNumber = translate(
        "This field is required.",
        "هذا الحقل مطلوب"
      );
    }

    if (!formData.blockNumber.trim()) {
      newErrors.blockNumber = translate(
        "This field is required.",
        "هذا الحقل مطلوب"
      );
    } else {
      // Only validate if we have the API data and the user has selected a block
      if (availableBlocks.length > 0) {
        const blockExists = availableBlocks.includes(formData.blockNumber);
        if (!blockExists) {
          newErrors.blockNumber = translate(
            "Please select a valid block number",
            "يرجى اختيار رقم مربع صحيح"
          );
        } else {
          // If block exists but no area found, show different error
          // Use debounced value to check if area lookup is complete
          if (
            debouncedBlockNumber === formData.blockNumber &&
            !formData.areaCode
          ) {
            newErrors.blockNumber = translate(
              "No area found for this block number",
              "لم يتم العثور على منطقة لرقم المربع هذا"
            );
          }
        }
      }
    }

    if (!formData.buildingAge.trim()) {
      newErrors.buildingAge = translate(
        "This field is required.",
        "هذا الحقل مطلوب"
      );
    } else if (parseInt(formData.buildingAge) <= 0) {
      newErrors.buildingAge = translate(
        "This field must be greater than 0",
        "يجب أن يكون هذا الحقل أكبر من 0"
      );
    } else if (parseInt(formData.buildingAge) > 15) {
      newErrors.buildingAge = translate(
        "The value must not exceed 15.",
        "يجب ألا تتجاوز القيمة 15."
      );
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Function to get quote from API using Redux
  const getQuote = async () => {
    try {
      // Prepare complete home form data including current address details
      const completeHomeData = {
        ...allHomeFormData,
        addressDetails: formData,
      };

      // Check if we already have a valid quote and data hasn't changed
      const hasValidQuote = reduxQuoteData && !reduxQuoteError;
      const currentDataString = JSON.stringify({
        homeDetails: completeHomeData.homeDetails,
        addressDetails: completeHomeData.addressDetails,
        covers: completeHomeData.covers,
      });

      console.log("=== Home Quote Check ===");
      console.log("Has valid quote:", hasValidQuote);
      console.log("Quote data exists:", !!reduxQuoteData);
      console.log("Quote error:", reduxQuoteError);

      // If we have a valid quote, use optimized call (will use cache if data matches)
      console.log(
        "Home Policy - Fetching home quote with party code:",
        partyCode || "FALCON"
      );

      await dispatch(
        fetchHomeQuote({
          homeFormData: completeHomeData,
          options: { forceRefresh: false }, // Use cached result if available
        })
      );

      console.log("✅ Home quote fetch completed");
    } catch (error) {
      console.error("Error dispatching quote:", error);
    }
  };

  const handleContinue = async () => {
    // Save form data to Redux
    saveHomeDataInRedux(formData);

    // Validate form before proceeding
    if (!validateForm()) {
      return;
    }

    // Check if user is logged in before proceeding
    if (!isLoggedIn) {
      setShowSignInModal(true);
      return;
    }

    // If we don't have quote data yet, user needs to wait for auto API call
    if (!quoteData && !isLoadingQuote) {
      // This shouldn't happen if auto-call is working, but just in case
      await getQuote();
      return;
    }

    // If currently loading, don't proceed
    if (isLoadingQuote) {
      return;
    }

    // If we have quote data, proceed to next step
    if (quoteData) {
      handleNextSubstep();
    }
  };

  const handleSignInSuccess = () => {
    setShowSignInModal(false);
    // After successful login, automatically proceed to next step
    handleNextSubstep();
  };

  return (
    <div className="w-full">
      {/* SignIn Modal */}
      <SignInModal
        show={showSignInModal}
        setShow={setShowSignInModal}
        cbSuccess={handleSignInSuccess}
      />

      <NewHomeJourneyLayout
        is_hide_back_btn={false}
        customBackHandler={handlePrevSubstep}
      >
        <BottomStickyBtnLayout
          btn_onClick={handleContinue}
          btn_label={translate("Continue", "التالي")}
          show_summary={quoteData && !fieldEdited ? true : false}
          price={quoteData ? quoteData.totalPremium : 0}
          passedFrom="HomeInsurance"
          isLoading={isLoadingQuote}
          btn_disabled={isLoadingQuote || !quoteData || fieldEdited}
          quoteData={quoteData}
          addonComponent={null}
        >
          <div className="flex justify-center items-start min-h-[60vh]">
            <div className="w-full max-w-[600px] mx-auto px-4">
              <TitleDescription
                title={translate("House Address", "عنوان المنزل")}
                description={translate(
                  "Please provide the necessary information below.",
                  "الرجاء ملء المعلومات التالية."
                )}
              />

              <div className="space-y-6 mt-8">
                {/* First Row - Building Number and Road Number */}
                <div className="grid grid-cols-2 md:grid-cols-2 gap-4">
                  <Input
                    label={translate("Building Number", "رقم المبنى")}
                    type="text"
                    name="buildingNumber"
                    value={formData.buildingNumber}
                    handleChange={handleInputChange}
                    error={errors.buildingNumber}
                    disabled={isLoadingQuote}
                  />
                  <Input
                    label={translate("Road Number", "رقم الشارع")}
                    type="text"
                    name="roadNumber"
                    value={formData.roadNumber}
                    handleChange={handleInputChange}
                    error={errors.roadNumber}
                    disabled={isLoadingQuote}
                  />
                </div>

                {/* Second Row - Block Number and Area */}
                <div className="grid grid-cols-2 md:grid-cols-2 gap-4">
                  <SelectInput
                    label={translate("Block Number", "رقم المربع")}
                    name="blockNumber"
                    formData={formData}
                    handleChange={handleInputChange}
                    options_data={blockOptions}
                    default_placeholder={
                      blocksAndAreasLoading
                        ? translate("Loading...", "جارٍ التحميل...")
                        : translate("Select", "رقم المربع")
                    }
                    error={errors.blockNumber}
                    disabled={isLoadingQuote}
                  />
                  <Input
                    label={translate("City", "المدينة")}
                    type="text"
                    name="areaName"
                    value={formData.areaName}
                    disabled={true}
                    handleChange={() => {}} // Disabled field, no handler needed
                  />
                </div>

                {/* Third Row - Building Age */}
                <div className="w-full">
                  <Input
                    label={translate("Building Age", "عمر المبنى")}
                    type="number"
                    name="buildingAge"
                    value={formData.buildingAge}
                    handleChange={handleInputChange}
                    error={errors.buildingAge}
                    disabled={isLoadingQuote}
                  />
                </div>
              </div>
            </div>
          </div>
        </BottomStickyBtnLayout>
      </NewHomeJourneyLayout>

      {/* Home Summary Modal */}
      <HomeSummaryModal
        show={showHomeSummaryModal}
        onClose={() => setShowHomeSummaryModal(false)}
        homeFormData={{
          ...allHomeFormData,
          addressDetails: formData,
        }}
        quoteData={quoteData}
      />
    </div>
  );
};

export default AddressDetails;
